import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Save, CheckCircle2, AlertCircle, Clock, XCircle } from "lucide-react";

export default function EmailSettings() {
  const [settings, setSettings] = useState(null);
  const [log, setLog] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [s, l] = await Promise.all([api.get("/cms/email-settings"), api.get("/cms/email-log")]);
    setSettings(s.data);
    setLog(l.data);
  };
  useEffect(() => { load(); }, []);
  if (!settings) return <div className="p-8">Lade...</div>;

  const set = (k, v) => setSettings({ ...settings, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: !!settings.enabled,
        smtp_host: settings.smtp_host || "",
        smtp_port: parseInt(settings.smtp_port) || 587,
        smtp_user: settings.smtp_user || "",
        smtp_password: settings.smtp_password || "",
        use_ssl: !!settings.use_ssl,
        from_email: settings.from_email || null,
        from_name: settings.from_name || "",
      };
      await api.put("/cms/email-settings", payload);
      setSettings({ ...settings, smtp_password: "" });
      load();
    } finally { setSaving(false); }
  };

  const STATUS_ICON = {
    sent: { Icon: CheckCircle2, color: "var(--mv-secondary)" },
    queued: { Icon: Clock, color: "var(--mv-accent)" },
    failed: { Icon: XCircle, color: "#dc2626" },
  };

  return (
    <div className="p-8 lg:p-12 mv-fade-in max-w-5xl">
      <div className="flex items-end justify-between mb-10">
        <div><p className="mv-label mb-2">Benachrichtigungen</p><h1 className="font-display text-4xl">E-Mail-Einstellungen</h1></div>
        <button data-testid="save-email-btn" onClick={save} disabled={saving} className="mv-btn"><Save className="w-4 h-4" /> {saving ? "..." : "Speichern"}</button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="mv-card p-6">
          <p className="mv-label mb-4">SMTP-Server</p>
          <label className="flex items-center gap-2 text-sm mb-4">
            <input type="checkbox" data-testid="email-enabled" checked={!!settings.enabled} onChange={(e) => set("enabled", e.target.checked)} />
            E-Mail-Versand aktivieren
          </label>
          <div className="space-y-3">
            <div><label className="block text-xs mb-1">SMTP-Host</label><input className="mv-input text-sm" value={settings.smtp_host || ""} onChange={(e) => set("smtp_host", e.target.value)} placeholder="smtp.example.com" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-xs mb-1">Port</label><input type="number" className="mv-input text-sm" value={settings.smtp_port || 587} onChange={(e) => set("smtp_port", e.target.value)} /></div>
              <label className="flex items-center gap-2 text-sm pt-6"><input type="checkbox" checked={!!settings.use_ssl} onChange={(e) => set("use_ssl", e.target.checked)} /> SSL/TLS</label>
            </div>
            <div><label className="block text-xs mb-1">Benutzer</label><input className="mv-input text-sm" value={settings.smtp_user || ""} onChange={(e) => set("smtp_user", e.target.value)} /></div>
            <div><label className="block text-xs mb-1">Passwort</label><input type="password" className="mv-input text-sm" placeholder="••••••••" value={settings.smtp_password || ""} onChange={(e) => set("smtp_password", e.target.value)} /></div>
          </div>
        </div>

        <div className="mv-card p-6">
          <p className="mv-label mb-4">Absender</p>
          <div className="space-y-3">
            <div><label className="block text-xs mb-1">From E-Mail</label><input type="email" className="mv-input text-sm" value={settings.from_email || ""} onChange={(e) => set("from_email", e.target.value)} placeholder="info@ihrsalon.de" /></div>
            <div><label className="block text-xs mb-1">From Name</label><input className="mv-input text-sm" value={settings.from_name || ""} onChange={(e) => set("from_name", e.target.value)} placeholder="Ihr Salon Name" /></div>
          </div>
          <div className="mt-6 p-4 rounded-lg text-xs leading-relaxed" style={{ background: "var(--mv-surface)", color: "var(--mv-text-muted)" }}>
            <p className="font-medium mb-1">Hinweis</p>
            E-Mails werden bei Buchungen (an Kunde + Salon) und Formular-Einsendungen (an Salon) versendet. Solange der Versand nicht aktiviert ist, werden Nachrichten im Log unten aufgezeichnet.
          </div>
        </div>
      </div>

      <div className="mt-8">
        <p className="mv-label mb-4">Versand-Protokoll (letzte 200)</p>
        <div className="mv-card overflow-hidden">
          {log.length === 0 ? (
            <p className="p-6 text-sm" style={{ color: "var(--mv-text-muted)" }}>Noch keine E-Mails versendet oder protokolliert.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: "var(--mv-surface)" }}>
                  <th className="text-left px-4 py-2.5 mv-label" style={{ color: "var(--mv-text-muted)" }}>Status</th>
                  <th className="text-left px-4 py-2.5 mv-label" style={{ color: "var(--mv-text-muted)" }}>An</th>
                  <th className="text-left px-4 py-2.5 mv-label" style={{ color: "var(--mv-text-muted)" }}>Betreff</th>
                  <th className="text-left px-4 py-2.5 mv-label" style={{ color: "var(--mv-text-muted)" }}>Datum</th>
                </tr>
              </thead>
              <tbody>
                {log.map((l) => {
                  const I = STATUS_ICON[l.status] || STATUS_ICON.queued;
                  return (
                    <tr key={l.id} className="border-t" style={{ borderColor: "var(--mv-border)" }}>
                      <td className="px-4 py-2.5">
                        <span className="inline-flex items-center gap-1 text-xs"><I.Icon className="w-3.5 h-3.5" strokeWidth={1.5} style={{ color: I.color }} />{l.status}</span>
                      </td>
                      <td className="px-4 py-2.5">{l.to}</td>
                      <td className="px-4 py-2.5 truncate max-w-md">{l.subject}{l.error && <span className="block text-xs text-red-500">{l.error}</span>}</td>
                      <td className="px-4 py-2.5 text-xs" style={{ color: "var(--mv-text-muted)" }}>{new Date(l.created_at).toLocaleString("de-DE")}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
